import { CheckCircleIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { Button, CircularProgress, HStack, Text } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { FileState } from "shared/components/file/FileState";

export interface FileListItemStateProps {
  state: FileState;
  progress: number | undefined;
  onClickRetry?: () => void;
  error?: string;
}

export const FileListItemState: React.FC<FileListItemStateProps> = ({
  state,
  progress,
  onClickRetry,
  error,
}) => {
  const { t } = useTranslation(["common"]);

  if (error) {
    return (
      <FileListItemStateFailed onClickRetry={onClickRetry}>
        {error}
      </FileListItemStateFailed>
    );
  }

  switch (state) {
    case "deleted":
      return (
        <FileListItemStateFailed onClickRetry={onClickRetry}>
          {t("common:upload.state.deleted")}
        </FileListItemStateFailed>
      );
    case "uploading":
      return (
        <FileListItemStateLoading progress={progress}>
          {t("common:upload.state.uploading")}
        </FileListItemStateLoading>
      );
    case "uploading_complete":
      return (
        <FileListItemStateSuccess>
          {t("common:upload.state.uploadingComplete")}
        </FileListItemStateSuccess>
      );
    case "uploading_failed":
      return (
        <FileListItemStateFailed onClickRetry={onClickRetry}>
          {t("common:upload.failed")}
        </FileListItemStateFailed>
      );
    case "uploading_cancelled":
      return (
        <FileListItemStateFailed onClickRetry={onClickRetry}>
          {t("common:upload.state.uploadingCancelled")}
        </FileListItemStateFailed>
      );
    case "processing":
      return (
        <FileListItemStateLoading progress={progress}>
          {t("common:upload.state.processing")}
        </FileListItemStateLoading>
      );
    case "processing_complete":
      return (
        <FileListItemStateSuccess>
          {t("common:upload.state.processingComplete")}
        </FileListItemStateSuccess>
      );
    case "processing_failed":
      return (
        <FileListItemStateFailed onClickRetry={onClickRetry}>
          {t("common:upload.state.processingFailed")}
        </FileListItemStateFailed>
      );
    case "processing_cancelled":
      return (
        <FileListItemStateFailed onClickRetry={onClickRetry}>
          {t("common:upload.state.processingCancelled")}
        </FileListItemStateFailed>
      );
  }

  return null;
};

const FileListItemStateLoading: React.FC<
  React.PropsWithChildren<{
    progress: number | undefined;
  }>
> = ({ progress, children }) => (
  <HStack spacing="8px">
    <CircularProgress
      size="14px"
      color="gray.500"
      isIndeterminate={!progress}
      value={progress}
    />
    <Text color="gray.500" variant="bodyBold">
      {children}
    </Text>
  </HStack>
);

const FileListItemStateSuccess: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <HStack spacing="8px">
    <CheckCircleIcon color="primary.600" />
    <Text color="primary.600" variant="bodyBold">
      {children}
    </Text>
  </HStack>
);

const FileListItemStateFailed: React.FC<
  React.PropsWithChildren<{
    onClickRetry: (() => void) | undefined;
  }>
> = ({ onClickRetry, children }) => {
  const { t } = useTranslation(["common"]);
  return (
    <HStack flexWrap="wrap" spacing={0} gap="8px">
      <HStack>
        <WarningTwoIcon />
        <Text variant="bodyBold">{children}</Text>
      </HStack>
      {onClickRetry && (
        <Button
          p={0}
          variant="subtle"
          colorScheme="destructive"
          textDecoration="underline !important"
          onClick={onClickRetry}
        >
          {t("common:upload.tryAgain")}
        </Button>
      )}
    </HStack>
  );
};
